import * as Sentry from "@sentry/react";

// ---------------------------------------------------------------------------
// Per-tab sede state for partner access.
//
// A partner works ONE company ("sede") of their group at a time. Which sede is
// active is a property of the tab, so it lives in `sessionStorage`:
// `localStorage.token` keeps the normal JWT untouched, because every other tab
// and the `apps/next` zone read it. This module never touches `localStorage`.
//
// Storage can be unavailable (blocked site data, private modes) or throw on
// any access. Every failure here means "no sede": the app then behaves exactly
// as it does for a user who is not a partner.
// ---------------------------------------------------------------------------

export const SEDE_COMPANY_ID_KEY = "partnerAccess.sedeCompanyId";
export const PARTNER_SESSION_ID_KEY = "partnerAccess.sessionId";

const COMPANY_ID_PATTERN = /^[0-9a-f]{24}$/;
const BREADCRUMB_CATEGORY = "partnerAccess";

const isCompanyId = (value) =>
  typeof value === "string" && COMPANY_ID_PATTERN.test(value);

const readItem = (key) => {
  try {
    return window.sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const writeItem = (key, value) => {
  try {
    window.sessionStorage.setItem(key, value);
    return true;
  } catch (e) {
    return false;
  }
};

const removeItem = (key) => {
  try {
    window.sessionStorage.removeItem(key);
  } catch (e) {
    // Nothing to remove from a storage that cannot be used.
  }
};

const recordTransition = (message, companyId) => {
  try {
    Sentry.addBreadcrumb({
      category: BREADCRUMB_CATEGORY,
      message,
      level: "info",
      data: { companyId },
    });
  } catch (e) {
    // Telemetry must never decide whether the tab is in sede mode.
  }
};

export const getActiveSedeId = () => {
  const stored = readItem(SEDE_COMPANY_ID_KEY);
  if (stored === null || stored === undefined) return null;
  if (isCompanyId(stored)) return stored;
  // A malformed id can only come from tampering or a bug: drop it so the tab
  // does not stay half in sede mode.
  removeItem(SEDE_COMPANY_ID_KEY);
  return null;
};

export const isSedeMode = () => getActiveSedeId() !== null;

export const getPartnerSessionId = () => readItem(PARTNER_SESSION_ID_KEY) || null;

export const setPartnerSessionId = (sessionId) => {
  if (typeof sessionId === "string" && sessionId !== "") {
    writeItem(PARTNER_SESSION_ID_KEY, sessionId);
    return;
  }
  removeItem(PARTNER_SESSION_ID_KEY);
};

/**
 * Makes `companyId` the active sede of this tab. Returns whether sede mode was
 * actually persisted, so a caller about to reload can tell a failed switch
 * from a successful one. Without `sessionId` the stored one is dropped and the
 * next token mint opens a new partner session.
 */
export const enterSedeMode = ({ companyId, sessionId } = {}) => {
  if (!isCompanyId(companyId)) return false;
  setPartnerSessionId(sessionId);
  if (!writeItem(SEDE_COMPANY_ID_KEY, companyId)) return false;
  recordTransition("sede.enter", companyId);
  return true;
};

export const leaveSedeMode = () => {
  const companyId = getActiveSedeId();
  removeItem(SEDE_COMPANY_ID_KEY);
  removeItem(PARTNER_SESSION_ID_KEY);
  if (companyId !== null) recordTransition("sede.leave", companyId);
};
